import { useSyncExternalStore } from 'react'

/**
 * Con qué se está usando el editor —el dedo o el ratón— y en qué pantalla.
 *
 * Es la pieza de la que cuelga todo lo táctil: el iPad ahora y, después, el
 * celular. Se publica en `<html>` como dos atributos, para que el CSS no tenga
 * que adivinar:
 *
 * - `data-input="touch" | "mouse"`: lo que se usó en el ÚLTIMO toque. No lo que
 *   el aparato dice tener: un iPad con teclado y trackpad es las dos cosas, y
 *   cambia de una a otra en mitad de la sesión. Arranca con lo que dice la
 *   pantalla (`hover: none` y puntero grueso) y se corrige con cada toque.
 * - `data-form-factor="phone" | "tablet" | "desktop"`: el tamaño de la VENTANA,
 *   no del aparato. Así achicar la ventana en la computadora da el editor de
 *   tableta, y más estrecha el de teléfono —se ve y se prueba sin un iPad—:
 *   - teléfono: menos de 640px de ancho, o táctil con el lado corto de menos
 *     de 600 (un teléfono tumbado sigue siendo un teléfono);
 *   - tableta: menos de 1180px, o cualquier pantalla táctil que no sea
 *     teléfono (un iPad Pro de 12,9" tumbado mide 1366);
 *   - escritorio: lo demás.
 *
 * ── Por qué hace falta saber el último toque ────────────────────────────────
 * Safari en iPad, al tocar, se inventa los eventos del ratón —entra, se mueve,
 * pulsa— para las páginas que sólo escuchan al ratón. El raíl abre su panel al
 * «pasar por encima» y lo alterna al pulsar: con el dedo, el toque lo abría con
 * el falso «entra» y lo cerraba con el clic, en el mismo gesto. Quien escucha
 * «entra» pregunta aquí si lo que viene es un dedo, y entonces no hace nada.
 */
export type InputKind = 'touch' | 'mouse'
export type FormFactor = 'phone' | 'tablet' | 'desktop'

type InputState = { input: InputKind; formFactor: FormFactor }

const PHONE_SHORT_SIDE = 600
export const PHONE_MAX_WIDTH = 640
export const TABLET_MAX_WIDTH = 1180

function prefersTouch(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches
}

export function formFactorFor(input: InputKind, width = typeof window === 'undefined' ? 0 : window.innerWidth, height = typeof window === 'undefined' ? 0 : window.innerHeight): FormFactor {
  if (!width) return 'desktop'
  const touch = input === 'touch' || prefersTouch()
  const shortSide = Math.min(width, height || width)
  if (width < PHONE_MAX_WIDTH || (touch && shortSide < PHONE_SHORT_SIDE)) return 'phone'
  if (width < TABLET_MAX_WIDTH || touch) return 'tablet'
  return 'desktop'
}

let state: InputState = { input: 'mouse', formFactor: 'desktop' }
const listeners = new Set<() => void>()

function publish(next: InputState) {
  if (next.input === state.input && next.formFactor === state.formFactor) return
  state = next
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.input = next.input
    document.documentElement.dataset.formFactor = next.formFactor
  }
  listeners.forEach((listener) => listener())
}

function setInput(input: InputKind) {
  publish({ input, formFactor: formFactorFor(input) })
}

/**
 * Lo que dice un evento de puntero. Un lápiz cuenta como dedo: no tiene hover
 * fiable y quiere los mismos blancos grandes.
 */
export function inputKindOf(pointerType: string | undefined): InputKind {
  return pointerType === 'mouse' ? 'mouse' : 'touch'
}

let installed = false

/** Se llama una vez al arrancar. Deja los atributos puestos y los sigue al día. */
export function installInputMode() {
  if (installed || typeof window === 'undefined') return
  installed = true
  const initial: InputKind = prefersTouch() ? 'touch' : 'mouse'
  state = { input: initial, formFactor: formFactorFor(initial) }
  document.documentElement.dataset.input = state.input
  document.documentElement.dataset.formFactor = state.formFactor
  // En captura: el que se entera primero, antes que cualquier `mouseenter` falso.
  window.addEventListener('pointerdown', (event) => {
    if (event.pointerType) setInput(inputKindOf(event.pointerType))
  }, true)
  window.addEventListener('resize', () => setInput(state.input))
}

/** El lienzo vive en un iframe y sus toques no llegan aquí: los avisa él. */
export function noteCanvasPointer(pointerType: string | undefined) {
  if (pointerType) setInput(inputKindOf(pointerType))
}

/** Si lo último fue un dedo. Para los `onMouseEnter` que Safari dispara al tocar. */
export function isTouchInput(): boolean {
  return state.input === 'touch'
}

export function currentFormFactor(): FormFactor {
  return state.formFactor
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** El modo al día, para los componentes que cambian de forma según él. */
export function useInputMode(): InputState {
  return useSyncExternalStore(subscribe, () => state, () => state)
}

/** Sólo para las pruebas: vuelve a empezar. */
export function resetInputModeForTests() {
  installed = false
  state = { input: 'mouse', formFactor: 'desktop' }
  listeners.clear()
}
